import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { authApi, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * ChangePinDialog
 * ------------------
 * - Nuvarande inloggningsuppgift: fritt lösenords-/PIN-fält (tillåter långa
 *   legacy-lösenord – INGEN numerisk begränsning, INGEN maxLength 4).
 * - Ny PIN-kod: exakt 4 siffror.
 * - Bekräfta ny PIN-kod: måste matcha.
 * - Vid lyckad ändring visas en tydlig bekräftelse om att andra enheter
 *   har loggats ut. useAuth.refreshMe() uppdaterar pinMigrated → menyvalet
 *   byter namn till "Byt PIN-kod".
 */
export default function ChangePinDialog({ open, onOpenChange }) {
  const { refreshMe, user } = useAuth();
  const alreadyMigrated = !!user?.pinMigrated;

  const [current, setCurrent] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Reset state when the dialog is opened/closed
  useEffect(() => {
    if (!open) {
      setCurrent("");
      setNewPin("");
      setConfirmPin("");
      setError("");
      setBusy(false);
    }
  }, [open]);

  const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 4);

  const validate = () => {
    if (!current) return "Ange nuvarande PIN-kod eller lösenord.";
    if (!/^\d{4}$/.test(newPin)) return "Ny PIN-kod måste vara exakt 4 siffror.";
    if (newPin !== confirmPin) return "PIN-koderna matchar inte.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setBusy(true);
    try {
      await authApi.setPin(current, newPin);
      toast.success(
        alreadyMigrated
          ? "PIN-kod uppdaterad. Andra enheter har loggats ut."
          : "Du använder nu PIN-kod. Andra enheter har loggats ut."
      );
      await refreshMe();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-pin-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#3B9EDA]" />
            {alreadyMigrated ? "Byt PIN-kod" : "Byt till PIN-kod"}
          </DialogTitle>
          <DialogDescription>
            {alreadyMigrated
              ? "Välj en ny fyrsiffrig PIN-kod. Dina andra enheter kommer loggas ut."
              : "Ersätt ditt nuvarande lösenord med en fyrsiffrig PIN-kod. Dina andra enheter kommer loggas ut."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" autoComplete="off">
          <div className="space-y-1.5">
            <Label htmlFor="current-credential">
              Nuvarande {alreadyMigrated ? "PIN-kod" : "PIN-kod eller lösenord"}
            </Label>
            <Input
              id="current-credential"
              data-testid="input-current-credential"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder={alreadyMigrated ? "4 siffror" : "PIN-kod eller tidigare lösenord"}
              maxLength={128}
              required
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-pin">Ny PIN-kod (4 siffror)</Label>
            <Input
              id="new-pin"
              data-testid="input-new-pin"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={newPin}
              onChange={(e) => setNewPin(onlyDigits(e.target.value))}
              placeholder="••••"
              maxLength={4}
              pattern="\d{4}"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-pin">Bekräfta ny PIN-kod</Label>
            <Input
              id="confirm-pin"
              data-testid="input-confirm-pin"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(onlyDigits(e.target.value))}
              placeholder="••••"
              maxLength={4}
              pattern="\d{4}"
              required
            />
          </div>

          {error && (
            <div
              data-testid="change-pin-error"
              className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200"
            >
              {error}
            </div>
          )}

          <div className="text-xs text-slate-500 flex items-start gap-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-[#3B9EDA]" />
            <span>
              PIN-koden måste vara unik i hela systemet. Vid lyckad ändring loggas
              dina andra sessioner ut automatiskt.
            </span>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              data-testid="btn-change-pin-cancel"
              className="rounded-full"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={busy || !current || newPin.length !== 4 || confirmPin.length !== 4}
              data-testid="btn-change-pin-submit"
              className="rounded-full text-white"
              style={{ backgroundColor: "#3B9EDA" }}
            >
              {busy ? "Sparar…" : (alreadyMigrated ? "Byt PIN-kod" : "Byt till PIN-kod")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
